import React from 'react';
import { Shift } from '../types/index';

interface ShiftCardProps {
  shift: Shift;
  onBook?: () => void;
  isBooked?: boolean;
  bookedRole?: string;
  canBook?: boolean;
  isSelected?: boolean;
  borderColor?: string;
  showTitle?: boolean;
  showLocation?: boolean;
  showAvailableSpots?: boolean;
  userRole?: string;
  showDate?: boolean;
  dateFormat?: 'raw' | 'pretty';
  timeLayout?: 'inline' | 'below';
}

const getTagColor = (tagName: string): string => {
  const name = tagName.toLowerCase();
  
  if (name.includes('night')) return 'bg-indigo-900 text-indigo-100';
  if (name.includes('evening')) return 'bg-purple-600 text-purple-100';
  if (name.includes('morning')) return 'bg-yellow-500 text-yellow-900';
  if (name.includes('music')) return 'bg-pink-600 text-pink-100';
  if (name.includes('day')) return 'bg-orange-500 text-orange-100';
  if (name.includes('weekend')) return 'bg-blue-600 text-blue-100';
  
  return 'bg-gray-600 text-gray-100';
};

export function ShiftCard({
  shift,
  onBook,
  isBooked,
  bookedRole,
  canBook,
  isSelected,
  borderColor,
  showTitle = true,
  showLocation = true,
  showAvailableSpots = true,
  userRole = 'volunteer',
  showDate = true,
  dateFormat = 'raw',
  timeLayout = 'inline',
}: ShiftCardProps) {
  // Times from API are already in configured timezone - just format for display
  const startParts = shift.start_time.substring(0, 16).split('T');
  const endParts = shift.end_time.substring(0, 16).split('T');
  const startDate = startParts[0];
  const startTime = startParts[1];
  const endTime = endParts[1];
  const isNextDay = startDate !== endParts[0];
  const timeLabel = `${startTime} - ${endTime}${isNextDay ? ' (next day)' : ''}`;
  const formatPrettyDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };
  const dateLabel = dateFormat === 'pretty' ? formatPrettyDate(startDate) : startDate;
  
  const hasRoleRequirements = Boolean(shift.role_requirements && shift.role_requirements.length > 0);
  const totalRoleSpots = hasRoleRequirements
    ? shift.role_requirements!.reduce((sum, req) => sum + (req.max_count || 0), 0)
    : 0;
  const totalRoleBooked = hasRoleRequirements
    ? shift.role_requirements!.reduce((sum, req) => sum + (req.current_count || 0), 0)
    : shift.current_volunteers;
  const isFull = (shift.manual_full === true) ? true : (totalRoleSpots > 0 ? totalRoleBooked >= totalRoleSpots : false);
  // If role-specific requirements exist, prefer showing remaining spots for the user's role
  let remainingSpots = 0;
  if (shift.manual_full === true) {
    remainingSpots = 0;
  } else if (hasRoleRequirements && userRole) {
    const roleReq = shift.role_requirements!.find((r: any) => r.role_name === userRole);
    if (roleReq) {
      const maxForRole = Number(roleReq.max_count || 0);
      const bookedForRole = Number(roleReq.current_count || 0);
      remainingSpots = Math.max(maxForRole - bookedForRole, 0);
    } else {
      remainingSpots = Math.max(totalRoleSpots - totalRoleBooked, 0);
    }
  } else {
    remainingSpots = totalRoleSpots > 0 ? Math.max(totalRoleSpots - totalRoleBooked, 0) : 0;
  }

  const formatRoleCapacity = () => `${remainingSpots} left`;
  const bookedRoleLabel = bookedRole ? bookedRole.charAt(0).toUpperCase() + bookedRole.slice(1) : '';
  const bookedRoleClass = bookedRole === 'volunteer'
    ? 'bg-orange-100 text-orange-800'
    : bookedRole === 'supervisor'
      ? 'bg-yellow-100 text-yellow-800'
      : 'bg-blue-100 text-blue-800';

  return (
    <div className={`bg-white rounded-lg shadow p-4 mb-3 ${borderColor ? borderColor : isSelected ? 'border-2 border-blue-500' : ''} ${isFull ? 'bg-gray-50' : ''}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-2">
          {isSelected && <span className="text-xl">✓</span>}
          <h3 className="text-lg font-semibold text-gray-900">
            {showTitle && shift.title ? `${shift.title} · ` : ''}{showDate ? `${dateLabel}` : ''}
            {timeLayout === 'inline' && ` ${timeLabel}`}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {isFull && (
            <span className="text-xs font-bold px-2 py-1 bg-red-100 text-red-700 rounded">
              FULL
            </span>
          )}
          {showAvailableSpots && (
            <span className="text-xs font-semibold text-gray-600">
              {formatRoleCapacity()}
            </span>
          )}
        </div>
      </div>

      {timeLayout === 'below' && (
        <p className="text-lg font-semibold text-gray-900 mb-1">{timeLabel}</p>
      )}

      {showLocation && shift.location && (
        <p className="text-sm text-gray-600 mb-2">📍 Meeting Point: {shift.location}</p>
      )}

      {((shift.tags && shift.tags.length > 0) || (isBooked && bookedRoleLabel)) && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3">
          {shift.tags && shift.tags.length > 0 && (
            <>
              {shift.tags.map((tag) => (
                <span
                  key={tag.id}
                  className={`text-xs px-2 py-1 rounded font-medium ${getTagColor(tag.name)}`}
                >
                  {tag.name}
                </span>
              ))}
            </>
          )}
          {isBooked && bookedRoleLabel && (
            <span className={`text-xs px-2 py-1 rounded font-medium ml-auto ${bookedRoleClass}`}>
              {bookedRoleLabel}
            </span>
          )}
        </div>
      )}

      {canBook && !isFull && onBook && (
        <button
          onClick={onBook}
          className="w-full px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 text-sm font-medium"
        >
          Book Shift
        </button>
      )}

      {!isBooked && !canBook && isSelected && (
        <div className={`w-full px-4 py-2 rounded text-center font-medium ${
          userRole === 'volunteer' ? 'bg-orange-100 text-orange-800' :
          userRole === 'supervisor' ? 'bg-yellow-100 text-yellow-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          Selected - Click to deselect
        </div>
      )}
    </div>
  );
}
